const STORIES_SELECTOR = '[data-type="storista"]';
const STORIES_GAP = 16;
const STORIES_AUTOPLAY_DELAY = 4500;
const STORIES_RESUME_DELAY = 6500;

const getStoriesItems = (grid) => [...grid.querySelectorAll('.storista-feed-grid-item')];

const getStoriesStep = (grid, items) => {
  const item = items[0];
  if (!item) return 0;

  const itemWidth = item.getBoundingClientRect().width;
  const styles = getComputedStyle(grid);
  const gap = Number.parseFloat(styles.columnGap || styles.gap) || STORIES_GAP;

  return itemWidth + gap;
};

const getStoriesPage = (grid, items) => {
  const step = getStoriesStep(grid, items);
  if (!step) return { current: 0, max: 0, visible: 1, step: 0 };

  const visible = Math.max(1, Math.round((grid.clientWidth + STORIES_GAP) / step));
  const max = Math.max(0, items.length - visible);
  const current = Math.min(max, Math.max(0, Math.round(grid.scrollLeft / step)));

  return { current, max, visible, step };
};

const enhanceStoriesWidget = (widget) => {
  const grid = widget.querySelector('.storista-feed-grid');
  const column = widget.querySelector('.storista-feed-column');
  const items = grid ? getStoriesItems(grid) : [];

  if (!grid || !column || items.length < 2 || grid.dataset.bumpersStoriesReady === 'true') return;

  grid.dataset.bumpersStoriesReady = 'true';
  grid.classList.add('bumpers-stories-carousel');
  widget.classList.add('bumpers-stories-widget');
  grid.setAttribute('aria-label', 'Bumpers stories');
  grid.setAttribute('tabindex', '0');

  const oldControls = column.querySelector('.bumpers-stories-controls');
  oldControls?.remove();

  const controls = document.createElement('div');
  controls.className = 'bumpers-stories-controls';
  controls.innerHTML = `
    <button type="button" class="bumpers-stories-control-button" data-direction="previous" aria-label="Previous stories">&#8592;</button>
    <span class="bumpers-stories-status" aria-live="polite"></span>
    <button type="button" class="bumpers-stories-control-button" data-direction="next" aria-label="Next stories">&#8594;</button>
  `;
  column.append(controls);

  const previousButton = controls.querySelector('[data-direction="previous"]');
  const nextButton = controls.querySelector('[data-direction="next"]');
  const status = controls.querySelector('.bumpers-stories-status');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let autoplayTimer;
  let resumeTimer;
  let dragging = false;
  let dragMoved = false;
  let dragStartX = 0;
  let dragStartScrollLeft = 0;

  const updateStatus = () => {
    const page = getStoriesPage(grid, items);
    const pageCount = Math.max(1, Math.ceil(items.length / page.visible));
    const currentPage = Math.floor(page.current / page.visible) + 1;
    if (status) status.textContent = `${currentPage} / ${pageCount}`;
  };

  const moveTo = (index) => {
    const page = getStoriesPage(grid, items);
    if (!page.step) return;

    const target = index < 0 ? page.max : index > page.max ? 0 : index;
    grid.scrollTo({ left: target * page.step, behavior: reducedMotion ? 'auto' : 'smooth' });
    window.setTimeout(updateStatus, reducedMotion ? 0 : 250);
  };

  const move = (direction) => {
    const page = getStoriesPage(grid, items);
    moveTo(page.current + direction * page.visible);
  };

  const stopAutoplay = () => {
    window.clearInterval(autoplayTimer);
    autoplayTimer = undefined;
  };

  const startAutoplay = () => {
    if (reducedMotion || autoplayTimer) return;
    autoplayTimer = window.setInterval(() => move(1), STORIES_AUTOPLAY_DELAY);
  };

  const pauseThenResume = () => {
    stopAutoplay();
    window.clearTimeout(resumeTimer);
    resumeTimer = window.setTimeout(startAutoplay, STORIES_RESUME_DELAY);
  };

  previousButton?.addEventListener('click', () => {
    move(-1);
    pauseThenResume();
  });

  nextButton?.addEventListener('click', () => {
    move(1);
    pauseThenResume();
  });

  grid.addEventListener('scroll', updateStatus, { passive: true });
  grid.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    move(event.key === 'ArrowRight' ? 1 : -1);
    pauseThenResume();
  });

  grid.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    dragging = true;
    dragMoved = false;
    dragStartX = event.clientX;
    dragStartScrollLeft = grid.scrollLeft;
    grid.classList.add('is-dragging');
    grid.setPointerCapture?.(event.pointerId);
    pauseThenResume();
  });

  grid.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    const delta = event.clientX - dragStartX;
    if (Math.abs(delta) > 6) dragMoved = true;
    if (dragMoved) {
      event.preventDefault();
      grid.scrollLeft = dragStartScrollLeft - delta;
    }
  });

  const stopDragging = (event) => {
    if (!dragging) return;
    dragging = false;
    grid.classList.remove('is-dragging');
    grid.releasePointerCapture?.(event.pointerId);
    if (dragMoved) {
      const page = getStoriesPage(grid, items);
      moveTo(page.current);
    }
  };

  grid.addEventListener('pointerup', stopDragging);
  grid.addEventListener('pointercancel', stopDragging);
  grid.addEventListener('mouseenter', stopAutoplay);
  grid.addEventListener('mouseleave', startAutoplay);
  grid.addEventListener('focusin', stopAutoplay);
  grid.addEventListener('focusout', startAutoplay);
  grid.addEventListener('touchstart', pauseThenResume, { passive: true });
  grid.addEventListener('touchend', pauseThenResume, { passive: true });

  updateStatus();
  startAutoplay();
};

const enhanceStories = () => {
  document.querySelectorAll(STORIES_SELECTOR).forEach(enhanceStoriesWidget);
};

enhanceStories();

const storiesObserver = new MutationObserver(enhanceStories);
storiesObserver.observe(document.body, { childList: true, subtree: true });
